import React, { useState } from 'react';
import {
  AppBar, Toolbar, Typography, Button, Box, Container, Grid, Paper, Avatar, TextField, IconButton, InputAdornment, Link as MuiLink, Fab, createTheme, ThemeProvider, CssBaseline, Switch, Dialog, DialogContent, DialogTitle, DialogActions
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import MovieFilterIcon from '@mui/icons-material/MovieFilter';
import DevicesIcon from '@mui/icons-material/Devices';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import EmailIcon from '@mui/icons-material/Email';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-scroll';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import emailjs from 'emailjs-com';

const services = [
  {
    icon: <TrendingUpIcon color="primary" sx={{ fontSize: 40 }} />, title: 'Social Media Management',
    desc: 'Grow your brand with expert social media strategies and daily engagement.'
  },
  {
    icon: <PeopleAltIcon color="primary" sx={{ fontSize: 40 }} />, title: 'Influencer Marketing',
    desc: 'Connect with top influencers to amplify your message and reach.'
  },
  {
    icon: <MovieFilterIcon color="primary" sx={{ fontSize: 40 }} />, title: 'High-Impact Content Production',
    desc: 'Stunning visuals and videos that captivate and convert.'
  },
  {
    icon: <DevicesIcon color="primary" sx={{ fontSize: 40 }} />, title: 'Digital Development',
    desc: 'Websites, landing pages, and digital tools to power your business.'
  },
  {
    icon: <AutoAwesomeIcon color="primary" sx={{ fontSize: 40 }} />, title: 'AI Automation for Leads',
    desc: 'Automate your lead generation and nurture process for maximum efficiency.'
  },
];

const testimonials = [
  {
    name: 'Sarah Lee',
    avatar: 'https://randomuser.me/api/portraits/women/44.jpg',
    text: 'Go Forcen transformed our online presence. Our leads and engagement skyrocketed!'
  },
  {
    name: 'James Smith',
    avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
    text: 'The content production and influencer campaigns were next level. Highly recommend!'
  },
  {
    name: 'Ava Brown',
    avatar: 'https://randomuser.me/api/portraits/women/65.jpg',
    text: 'Professional, creative, and results-driven. Go Forcen is our go-to agency.'
  },
];

const clientLogos = [
  // Google
  'https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg',
  // n8n
  'https://avatars.githubusercontent.com/u/45487711?s=200&v=4',
  // Zapier
  'https://cdn.worldvectorlogo.com/logos/zapier.svg',
  // Supabase
  'https://seeklogo.com/images/S/supabase-logo-DCC676FFE2-seeklogo.com.png',
  // Medusa
  'https://avatars.githubusercontent.com/u/51428282?s=200&v=4',
  
];

const ContactSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
  email: Yup.string().email('Invalid email').required('Required'),
  message: Yup.string().min(10, 'Too Short!').required('Required'),
});

const navLinks = [
  { label: 'Home', to: 'hero' },
  { label: 'Services', to: 'services' },
  { label: 'Testimonials', to: 'testimonials' },
  // { label: 'Contact', to: 'contact' },
];

const sectionFade = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7 } },
};

const glass = {
  background: 'rgba(255,255,255,0.18)',
  boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.18)',
  backdropFilter: 'blur(12px)',
  border: '1px solid rgba(255,255,255,0.25)',
};

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#3b82f6' },
    secondary: { main: '#60a5fa' },
    background: { default: '#101624', paper: '#1e2636' },
    text: {
      primary: '#e5e7eb',
      secondary: '#a5b4cb',
    },
    divider: '#23304a',
  },
  typography: {
    fontFamily: 'Montserrat, sans-serif',
    fontWeightBold: 900,
  },
});

const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#2563eb' },
    secondary: { main: '#38bdf8' },
    background: { default: '#f5f7fa', paper: '#fff' },
  },
  typography: {
    fontFamily: 'Montserrat, sans-serif',
    fontWeightBold: 900,
  },
});

const animateCounter = (start: number, end: number, duration: number, setDisplay: (n: number) => void) => {
  const startTime = performance.now();
  function step(now: number) {
    const elapsed = now - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const current = Math.round(start + (end - start) * progress);
    setDisplay(current);
    if (progress < 1) {
      requestAnimationFrame(step);
    }
  }
  requestAnimationFrame(step);
};

const AnimatedCounter: React.FC<{ value: number; label: string }> = ({ value, label }) => {
  const ref = React.useRef(null);
  const inView = useInView(ref, { once: true });
  const [display, setDisplay] = useState(0);

  React.useEffect(() => {
    if (inView) {
      animateCounter(0, value, 2000, setDisplay);
    }
  }, [inView, value]);

  return (
    <Box ref={ref} sx={{ textAlign: 'center', mb: 2 }}>
      <Typography variant="h3" fontWeight={800} color="primary">
        {display}
      </Typography>
      <Typography variant="subtitle1" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
};

const whyForcen = [
  {
    icon: <AutoAwesomeIcon color="secondary" sx={{ fontSize: 40 }} />,
    title: 'Unmatched Momentum',
    desc: 'We drive your brand forward with relentless energy and innovation.'
  },
  {
    icon: <TrendingUpIcon color="primary" sx={{ fontSize: 40 }} />,
    title: 'Results-Driven',
    desc: 'Our strategies are engineered for measurable impact and growth.'
  },
  {
    icon: <DevicesIcon color="secondary" sx={{ fontSize: 40 }} />,
    title: 'Cutting-Edge Digital',
    desc: 'We harness the latest tech to keep you ahead of the curve.'
  },
];

// Custom SVG Logo
const GoForcenLogo = ({ size = 40 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="24" cy="24" r="24" fill="#7f5af0" />
    <path d="M12 36L36 12" stroke="#fff" strokeWidth="4" strokeLinecap="round" />
    <path d="M24 12V36" stroke="#2cb67d" strokeWidth="4" strokeLinecap="round" />
    <path d="M12 24H36" stroke="#fff" strokeWidth="4" strokeLinecap="round" />
  </svg>
);

const App: React.FC = () => {
  const [dark, setDark] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const theme = dark ? darkTheme : lightTheme;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box>
        {/* Sticky Navbar */}
        <AppBar position="sticky" color="inherit" elevation={1} sx={{ backdropFilter: 'blur(8px)' }}>
          <Toolbar>
            <Box sx={{ mr: 1 }}><GoForcenLogo size={36} /></Box>
            <Typography variant="h5" sx={{ flexGrow: 1, fontWeight: 700, color: theme.palette.mode === 'dark' ? '#fff' : theme.palette.primary.main, letterSpacing: 1 }}>
              Go Forcen
            </Typography>
            <Switch checked={dark} onChange={() => setDark((d) => !d)} color="primary" sx={{ mr: 2 }} />
            {navLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                smooth
                duration={600}
                offset={-70}
                style={{ textDecoration: 'none' }}
              >
                <Button color="primary" sx={{ mx: 1, fontWeight: 600 }}>
                  {link.label}
                </Button>
              </Link>
            ))}
          </Toolbar>
        </AppBar>

        {/* Animated Hero Section with Parallax SVG */}
        <Box id="hero" sx={{
          background: dark
            ? '#23293a'
            : '#2563eb',
          color: '#fff',
          py: { xs: 10, md: 16 },
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}>
          {/* No SVG wave, keep background clean */}
          <motion.div
            initial={{ opacity: 0, y: -60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            style={{ position: 'relative', zIndex: 1 }}
          >
            <Container maxWidth="md">
              <Typography variant="h2" fontWeight={900} gutterBottom sx={{ letterSpacing: 2, fontSize: { xs: 36, md: 56 } }}>
                Unleash Your Brand's Force
              </Typography>
              <Typography variant="h5" sx={{ mb: 5, fontWeight: 500, fontSize: { xs: 18, md: 28 }, maxWidth: 600, mx: 'auto' }}>
Go Forcen is a results-driven marketing agency that fuels your growth with strategy, creativity, technology, and AI. Experience the power of momentum.              </Typography>
              <Button variant="contained" size="large" color="secondary" sx={{ fontWeight: 700, px: 5, py: 1.5, boxShadow: 3, fontSize: 20 }} onClick={() => setContactOpen(true)}>
                Accelerate Now
              </Button>
            </Container>
          </motion.div>
        </Box>

        {/* Why Go Forcen Section */}
        <Box sx={{ py: { xs: 7, md: 10 }, background: dark ? '#181c24' : '#f7fafd' }}>
          <Container>
            <Typography variant="h4" fontWeight={900} align="center" gutterBottom color="primary">
              Why Go Forcen?
            </Typography>
            <Grid container spacing={4} justifyContent="center">
              {whyForcen.map((item, idx) => (
                <Grid item xs={12} sm={6} md={4} key={item.title}>
                  <motion.div
                    whileHover={{ scale: 1.07 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                  >
                    <Paper elevation={0} sx={{ ...glass, p: 4, textAlign: 'center', borderRadius: 4, height: '100%', transition: 'box-shadow 0.3s' }}>
                      <Box mb={2}>{item.icon}</Box>
                      <Typography variant="h6" fontWeight={800} gutterBottom>
                        {item.title}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        {item.desc}
                      </Typography>
                    </Paper>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Animated Counters Section */}
        <Box sx={{ py: { xs: 5, md: 8 }, background: 'transparent' }}>
          <Container>
            <Grid container spacing={4} justifyContent="center">
              <Grid item xs={6} md={3}>
                <AnimatedCounter value={80} label="Happy Clients" />
              </Grid>
              <Grid item xs={6} md={3}>
                <AnimatedCounter value={172} label="Projects Completed" />
              </Grid>
              {/* <Grid item xs={6} md={3}>
                <AnimatedCounter value={15} label="Awards Won" />
              </Grid>
              <Grid item xs={6} md={3}>
                <AnimatedCounter value={8} label="Years Experience" />
              </Grid> */}
            </Grid>
          </Container>
        </Box>
 {/* Trusted by Section */}
        <Box sx={{ py: 4, background: 'transparent' }}>
          <Container>
            <Typography variant="subtitle1" align="center" color="text.secondary" sx={{ mb: 2, fontWeight: 600, letterSpacing: 1 }}>
              Collaboration with
            </Typography>
            <Grid container spacing={2} justifyContent="center" alignItems="center">
              {clientLogos.map((logo, idx) => (
                <Grid item key={idx} xs={4} sm={2} md={2} sx={{ textAlign: 'center' }}>
                  <motion.img
                    src={logo}
                    alt={`Client logo ${idx + 1}`}
                    style={{ width: 80, height: 40, objectFit: 'contain', filter: 'grayscale(1)', opacity: 0.7 }}
                    whileHover={{ scale: 1.15, filter: 'grayscale(0)', opacity: 1 }}
                    transition={{ type: 'spring', stiffness: 200 }}
                  />
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
        {/* Client Logos Section
        <Box sx={{ py: 3, background: 'transparent' }}>
          <Container>
            <Grid container spacing={2} justifyContent="center" alignItems="center">
              {clientLogos.map((logo, idx) => (
                <Grid item key={idx} xs={4} sm={2} md={2} sx={{ textAlign: 'center' }}>
                  <motion.img
                    src={logo}
                    alt={`Client logo ${idx + 1}`}
                    style={{ width: 80, height: 40, objectFit: 'contain', filter: 'grayscale(1)', opacity: 0.7 }}
                    whileHover={{ scale: 1.15, filter: 'grayscale(0)', opacity: 1 }}
                    transition={{ type: 'spring', stiffness: 200 }}
                  />
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box> */}

        {/* Services Section with Glassmorphism */}
        <Box id="services" component={motion.div} variants={sectionFade} initial="hidden" whileInView="visible" viewport={{ once: true }} sx={{ py: { xs: 7, md: 10 }, background: dark ? '#181c24' : '#f5f7fa' }}>
          <Container>
            <Typography variant="h4" fontWeight={900} align="center" gutterBottom>
              Our Services
            </Typography>
            <Grid container spacing={4} justifyContent="center">
              {services.map((service, idx) => (
                <Grid item xs={12} sm={6} md={4} key={service.title}>
                  <motion.div
                    whileHover={{ scale: 1.05, boxShadow: '0 8px 32px rgba(33,150,243,0.15)' }}
                    transition={{ type: 'spring', stiffness: 300 }}
                  >
                    <Paper elevation={0} sx={{ ...glass, p: 4, textAlign: 'center', borderRadius: 4, height: '100%', transition: 'box-shadow 0.3s' }}>
                      <Box mb={2}>{service.icon}</Box>
                      <Typography variant="h6" fontWeight={700} gutterBottom>
                        {service.title}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        {service.desc}
                      </Typography>
                    </Paper>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Testimonials Section with Glassmorphism */}
        <Box id="testimonials" component={motion.div} variants={sectionFade} initial="hidden" whileInView="visible" viewport={{ once: true }} sx={{ py: { xs: 7, md: 10 }, background: dark ? '#23293a' : '#fff' }}>
          <Container>
            <Typography variant="h4" fontWeight={900} align="center" gutterBottom>
              What Our Clients Say
            </Typography>
            <Grid container spacing={4} justifyContent="center">
              {testimonials.map((t, idx) => (
                <Grid item xs={12} sm={6} md={4} key={t.name}>
                  <motion.div
                    whileHover={{ scale: 1.04 }}
                    transition={{ type: 'spring', stiffness: 200 }}
                  >
                    <Paper elevation={0} sx={{ ...glass, p: 4, borderRadius: 4, textAlign: 'center', height: '100%' }}>
                      <Avatar src={t.avatar} alt={t.name} sx={{ width: 64, height: 64, mx: 'auto', mb: 2 }} />
                      <Typography variant="body1" sx={{ fontStyle: 'italic', mb: 2 }}>
                        "{t.text}"
                      </Typography>
                      <Typography variant="subtitle1" fontWeight={700} color="primary">
                        {t.name}
                      </Typography>
                    </Paper>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

       

        {/* Our Work / Case Studies Section
        <Box sx={{ py: { xs: 7, md: 10 }, background: dark ? '#181c24' : '#f7fafd' }}>
          <Container>
            <Typography variant="h4" fontWeight={900} align="center" gutterBottom color="primary">
              Our Work
            </Typography>
            <Grid container spacing={4} justifyContent="center">
              <Grid item xs={12} sm={6} md={4}>
                <Paper elevation={0} sx={{ ...glass, p: 3, borderRadius: 4, textAlign: 'left' }}>
                  <Box sx={{ mb: 2, height: 120, background: 'linear-gradient(90deg, #7f5af0 0%, #2cb67d 100%)', borderRadius: 2 }} />
                  <Typography variant="h6" fontWeight={800} gutterBottom>Force Fitness Campaign</Typography>
                  <Typography variant="body2" color="text.secondary">Doubled online engagement and generated 2,000+ new leads in 3 months for a fitness brand.</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Paper elevation={0} sx={{ ...glass, p: 3, borderRadius: 4, textAlign: 'left' }}>
                  <Box sx={{ mb: 2, height: 120, background: 'linear-gradient(90deg, #2cb67d 0%, #7f5af0 100%)', borderRadius: 2 }} />
                  <Typography variant="h6" fontWeight={800} gutterBottom>Tech Momentum Launch</Typography>
                  <Typography variant="body2" color="text.secondary">Helped a SaaS startup achieve 300% growth in signups with a viral product launch.</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Paper elevation={0} sx={{ ...glass, p: 3, borderRadius: 4, textAlign: 'left' }}>
                  <Box sx={{ mb: 2, height: 120, background: 'linear-gradient(90deg, #7f5af0 0%, #2cb67d 100%)', borderRadius: 2 }} />
                  <Typography variant="h6" fontWeight={800} gutterBottom>Influencer Power Drive</Typography>
                  <Typography variant="body2" color="text.secondary">Orchestrated a multi-influencer campaign for a beauty brand, reaching 1M+ new customers.</Typography>
                </Paper>
              </Grid>
            </Grid>
          </Container>
        </Box> */}

        {/* Meet the Team Section
        <Box sx={{ py: { xs: 7, md: 10 }, background: 'transparent' }}>
          <Container>
            <Typography variant="h4" fontWeight={900} align="center" gutterBottom color="primary">
              Meet the Team
            </Typography>
            <Grid container spacing={4} justifyContent="center">
              <Grid item xs={12} sm={6} md={3}>
                <Paper elevation={0} sx={{ ...glass, p: 3, borderRadius: 4, textAlign: 'center' }}>
                  <Avatar src="https://randomuser.me/api/portraits/men/32.jpg" alt="James Smith" sx={{ width: 72, height: 72, mx: 'auto', mb: 2 }} />
                  <Typography variant="subtitle1" fontWeight={800}>James Smith</Typography>
                  <Typography variant="body2" color="text.secondary">CEO & Strategy Lead</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Visionary leader with 10+ years in digital marketing and business growth.</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper elevation={0} sx={{ ...glass, p: 3, borderRadius: 4, textAlign: 'center' }}>
                  <Avatar src="https://randomuser.me/api/portraits/women/44.jpg" alt="Sarah Lee" sx={{ width: 72, height: 72, mx: 'auto', mb: 2 }} />
                  <Typography variant="subtitle1" fontWeight={800}>Sarah Lee</Typography>
                  <Typography variant="body2" color="text.secondary">Creative Director</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Award-winning creative with a passion for storytelling and design.</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper elevation={0} sx={{ ...glass, p: 3, borderRadius: 4, textAlign: 'center' }}>
                  <Avatar src="https://randomuser.me/api/portraits/men/45.jpg" alt="Alex Kim" sx={{ width: 72, height: 72, mx: 'auto', mb: 2 }} />
                  <Typography variant="subtitle1" fontWeight={800}>Alex Kim</Typography>
                  <Typography variant="body2" color="text.secondary">Tech & Automation</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Automation expert and full-stack developer driving digital innovation.</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper elevation={0} sx={{ ...glass, p: 3, borderRadius: 4, textAlign: 'center' }}>
                  <Avatar src="https://randomuser.me/api/portraits/women/65.jpg" alt="Ava Brown" sx={{ width: 72, height: 72, mx: 'auto', mb: 2 }} />
                  <Typography variant="subtitle1" fontWeight={800}>Ava Brown</Typography>
                  <Typography variant="body2" color="text.secondary">Influencer Marketing</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Influencer strategist with a global network and proven results.</Typography>
                </Paper>
              </Grid>
            </Grid>
          </Container>
        </Box> */}

        {/* Contact Modal */}
        <Dialog open={contactOpen} onClose={() => setContactOpen(false)} aria-labelledby="contact-dialog-title" maxWidth="sm" fullWidth>
          <DialogTitle id="contact-dialog-title" sx={{ fontWeight: 900, textAlign: 'center', color: 'primary.main' }}>Contact Go Forcen</DialogTitle>
          <DialogContent sx={{ background: dark ? '#23293a' : '#2563eb', color: '#fff', p: 4 }}>
            <Formik
              initialValues={{ name: '', email: '', message: '' }}
              validationSchema={ContactSchema}
              onSubmit={async (values, { resetForm, setSubmitting }) => {
                try {
                  const serviceID = 'service_dxvufmo';
                  const templateID = 'template_1cc8jgr';
                  const userID = '1anXR_x0oIkkiPijs';
                  await emailjs.send(serviceID, templateID, {
                    name: values.name,
                    email: values.email,
                    message: values.message,
                  }, userID);
                  alert('Thank you for contacting Go Forcen! Your message has been sent.');
                  resetForm();
                  setContactOpen(false);
                } catch (error) {
                  alert('Sorry, there was an error sending your message. Please try again later.');
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {({ errors, touched, isSubmitting }) => (
                <Form>
                  <Field name="name">
                    {({ field }: any) => (
                      <TextField
                        {...field}
                        label="Name"
                        variant="filled"
                        fullWidth
                        sx={{ mb: 2, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 1 }}
                        error={!!errors.name && touched.name}
                        helperText={touched.name && errors.name}
                        InputProps={{ style: { color: '#fff' } }}
                        InputLabelProps={{ style: { color: '#fff' } }}
                      />
                    )}
                  </Field>
                  <Field name="email">
                    {({ field }: any) => (
                      <TextField
                        {...field}
                        label="Email"
                        variant="filled"
                        fullWidth
                        sx={{ mb: 2, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 1 }}
                        error={!!errors.email && touched.email}
                        helperText={touched.email && errors.email}
                        InputProps={{
                          style: { color: '#fff' },
                          endAdornment: (
                            <InputAdornment position="end">
                              <EmailIcon sx={{ color: '#fff' }} />
                            </InputAdornment>
                          ),
                        }}
                        InputLabelProps={{ style: { color: '#fff' } }}
                      />
                    )}
                  </Field>
                  <Field name="message">
                    {({ field }: any) => (
                      <TextField
                        {...field}
                        label="Message"
                        variant="filled"
                        fullWidth
                        multiline
                        minRows={4}
                        sx={{ mb: 2, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 1 }}
                        error={!!errors.message && touched.message}
                        helperText={touched.message && errors.message}
                        InputProps={{ style: { color: '#fff' } }}
                        InputLabelProps={{ style: { color: '#fff' } }}
                      />
                    )}
                  </Field>
                  <Button
                    type="submit"
                    variant="contained"
                    color="secondary"
                    size="large"
                    fullWidth
                    disabled={isSubmitting}
                    sx={{ fontWeight: 700, px: 5, py: 1.5, mt: 1 }}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </Form>
              )}
            </Formik>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
            <Button onClick={() => setContactOpen(false)} color="primary" variant="text">Close</Button>
          </DialogActions>
        </Dialog>

        {/* Footer with Socials, Privacy, and Terms */}
        <Box sx={{ py: 4, textAlign: 'center', color: theme.palette.mode === 'dark' ? 'grey.400' : 'grey.700', background: theme.palette.background.paper, borderTop: '1px solid #e3e3e3' }}>
          <Container>
            <Box sx={{ mb: 2 }}>
              <IconButton component={MuiLink} href="https://instagram.com" target="_blank" rel="noopener" aria-label="Instagram">
                <InstagramIcon color="primary" />
              </IconButton>
              <IconButton component={MuiLink} href="https://facebook.com" target="_blank" rel="noopener" aria-label="Facebook">
                <FacebookIcon color="primary" />
              </IconButton>
              <IconButton component={MuiLink} href="https://twitter.com" target="_blank" rel="noopener" aria-label="Twitter">
                <TwitterIcon color="primary" />
              </IconButton>
            </Box>
            <Box sx={{ mb: 1 }}>
              <MuiLink href="#" underline="hover" color="inherit" sx={{ mx: 1, fontSize: 14 }}>Privacy Policy</MuiLink>
              <MuiLink href="#" underline="hover" color="inherit" sx={{ mx: 1, fontSize: 14 }}>Terms of Service</MuiLink>
            </Box>
            <Typography variant="body2">
              © {new Date().getFullYear()} Go Forcen Marketing Agency. All rights reserved.
            </Typography>
          </Container>
        </Box>

        {/* Floating Action Button for Scroll to Top */}
        <Fab
          color="primary"
          size="medium"
          aria-label="scroll back to top"
          sx={{ position: 'fixed', bottom: 32, right: 32, zIndex: 2000, boxShadow: 4 }}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          <KeyboardArrowUpIcon />
        </Fab>
      </Box>
    </ThemeProvider>
  );
};

export default App; 